/**
 * UserPicker — search the platform user directory by handle and pick one.
 * Rows come from the SHARED profile-directory context (the one
 * `creatorProfiles` subscription — no listener of its own). Used by the
 * collaborator invite flow: the caller passes the uids already on the roster
 * via `exclude` so current members never show up as invitable.
 */
import { useState } from 'react';
import type { CreatorProfile } from '@djgg/creations';
import { useProfileDirectory } from '../services/context.js';
import { UserAvatar } from './UserAvatar.js';

/** A directory row — the profile doc plus its id (the owner's uid). */
export type PickableProfile = CreatorProfile & { id: string };

/** Cap the rendered list so the picker stays a picker, not a directory dump. */
const MAX_RESULTS = 8;

export interface UserPickerProps {
  /** Uids to hide (already-members: the creator + current editors). */
  exclude: ReadonlySet<string>;
  onPick: (profile: PickableProfile) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function UserPicker({ exclude, onPick, placeholder, disabled }: UserPickerProps) {
  const { profiles } = useProfileDirectory();
  const [q, setQ] = useState('');

  const needle = q.trim().toLowerCase();
  const matches = profiles
    .filter((p) => !exclude.has(p.id))
    .filter((p) => !needle || (p.handle || p.id).toLowerCase().includes(needle))
    .sort((a, b) => (a.handle || a.id).localeCompare(b.handle || b.id))
    .slice(0, MAX_RESULTS);

  return (
    <div data-testid="user-picker">
      <input
        className="input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder ?? 'Search users by handle…'}
        disabled={disabled}
        aria-label="Search users by handle"
      />
      <div className="stack" style={{ marginTop: 8 }}>
        {matches.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}
            onClick={() => onPick(p)}
            disabled={disabled}
            data-testid={`user-pick-${p.id}`}
          >
            <UserAvatar avatarKey={p.avatarKey} size={20} />
            @{p.handle || p.id}
            {p.role && (
              <span className={`role-badge${p.role === 'superadmin' ? ' role-badge--admin' : ''}`}>
                {p.role}
              </span>
            )}
          </button>
        ))}
        {matches.length === 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            No matching users.
          </span>
        )}
      </div>
    </div>
  );
}
